import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Trash2, Plus, Minus, ShoppingBag } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { formatINR } from '../utils/currency';

const BACKEND_URL = (typeof window !== 'undefined' && window.location.origin) || process.env.REACT_APP_BACKEND_URL;

const Cart = () => {
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [discountCode, setDiscountCode] = useState('');
  const [discountApplied, setDiscountApplied] = useState(null);
  const [discountError, setDiscountError] = useState('');
  const [showThankYouModal, setShowThankYouModal] = useState(false);
  const [deliveryAddress, setDeliveryAddress] = useState({
    firstName: '',
    lastName: '',
    address: '',
    apartment: '',
    city: '',
    state: '',
    pinCode: '',
    saveAddress: false
  });
  const [addressError, setAddressError] = useState('');
  const { user } = useAuth();
  const navigate = useNavigate();

  const fetchCart = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/cart`, {
        withCredentials: true
      });
      setCart(data);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Error fetching cart:', error);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchCart();
    } else {
      setLoading(false);
    }
  }, [user, fetchCart]);

  const updateQuantity = async (productId, newQuantity) => {
    // If quantity is 0 or less, remove the item instead
    if (newQuantity < 1) {
      await removeItem(productId);
      return;
    }
    
    try {
      await axios.put(
        `${BACKEND_URL}/api/cart/update/${productId}?quantity=${newQuantity}`,
        {},
        { withCredentials: true }
      );
      await fetchCart();
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Error updating quantity:', error);
      alert('Failed to update quantity');
    }
  };

  const removeItem = async (productId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/cart/remove/${productId}`, {
        withCredentials: true
      });
      await fetchCart();
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Error removing item:', error);
      alert('Failed to remove item');
    }
  };

  const clearCart = async () => {
    if (!window.confirm('Are you sure you want to clear your entire cart?')) {
      return;
    }
    
    try {
      await axios.delete(`${BACKEND_URL}/api/cart/clear`, {
        withCredentials: true
      });
      await fetchCart();
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Error clearing cart:', error);
      alert('Failed to clear cart');
    }
  };

  const applyDiscountCode = async () => {
    if (!discountCode.trim()) {
      setDiscountError('Please enter a discount code');
      return;
    }

    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/validate-discount-code`,
        { code: discountCode.trim() },
        { withCredentials: true }
      );
      
      setDiscountApplied(data);
      setDiscountError('');
    } catch (error) {
      setDiscountApplied(null);
      setDiscountError(error.response?.data?.detail || 'Invalid discount code');
    }
  };

  const removeDiscountCode = () => {
    setDiscountCode('');
    setDiscountApplied(null);
    setDiscountError('');
  };

  const handleUpiPaymentComplete = async () => {
    if (cart.items.length === 0) return;
    
    // Validate delivery address
    if (!deliveryAddress.lastName || !deliveryAddress.address || 
        !deliveryAddress.city || !deliveryAddress.state || !deliveryAddress.pinCode) {
      setAddressError('Please fill in all required delivery address fields');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    
    try {
      setProcessing(true);
      setAddressError('');
      
      // Create UPI order with delivery address
      const { data } = await axios.post(
        `${BACKEND_URL}/api/checkout/upi`,
        { 
          discount_code: discountApplied ? discountApplied.code : null,
          delivery_address: deliveryAddress
        },
        { withCredentials: true }
      );
      
      // Show success modal
      setShowThankYouModal(true);
      
      // Refresh cart (should be empty now)
      await fetchCart();
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('UPI order creation error:', error);
      }
      alert('Failed to create order. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <ShoppingBag className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h1 className="text-3xl font-bold mb-4">Your Cart</h1>
          <p className="text-gray-600 mb-6">Please login to view your cart</p>
          <button
            onClick={() => navigate('/login')}
            className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition"
          >
            Login
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold" data-testid="cart-title">Shopping Cart</h1>
          {cart.items.length > 0 && (
            <button
              onClick={clearCart}
              className="text-red-600 hover:text-red-700 font-semibold transition flex items-center space-x-2"
              data-testid="clear-cart-button"
            >
              <Trash2 className="w-5 h-5" />
              <span>Clear Cart</span>
            </button>
          )}
        </div>

        {cart.items.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <ShoppingBag className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-gray-700 mb-2">Your cart is empty</h2>
            <p className="text-gray-600 mb-6">Start shopping to add items to your cart</p>
            <button
              onClick={() => navigate('/products')}
              className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition"
            >
              Browse Products
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Cart Items */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              {cart.items.map((item) => (
                <div
                  key={item.product_id}
                  className="flex items-center p-4 border-b last:border-b-0"
                  data-testid={`cart-item-${item.product_id}`}
                >
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-24 h-24 object-cover rounded-lg"
                  />
                  <div className="flex-1 ml-4">
                    <h3 className="text-lg font-semibold">{item.name}</h3>
                    <p className="text-purple-600 font-semibold">{formatINR(item.price)}</p>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2 border rounded-lg">
                      <button
                        onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                        className={`p-2 transition ${
                          item.quantity === 1 
                            ? 'hover:bg-red-100 text-red-600' 
                            : 'hover:bg-gray-100'
                        }`}
                        title={item.quantity === 1 ? 'Remove item' : 'Decrease quantity'}
                        data-testid={`decrease-quantity-${item.product_id}`}
                      >
                        {item.quantity === 1 ? (
                          <Trash2 className="w-4 h-4" />
                        ) : (
                          <Minus className="w-4 h-4" />
                        )}
                      </button>
                      <span className="px-4 font-semibold">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                        className="p-2 hover:bg-gray-100 transition"
                        data-testid={`increase-quantity-${item.product_id}`}
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="text-lg font-bold">
                      {formatINR(item.item_total)}
                    </div>
                    <button
                      onClick={() => removeItem(item.product_id)}
                      className="text-red-600 hover:text-red-700 transition"
                      title="Remove item"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Cart Summary */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-2xl font-bold mb-4">Order Summary</h2>
              
              {/* Delivery Address Section */}
              <div className="mb-6 pb-6 border-b">
                <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
                  <svg className="w-5 h-5 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Delivery Address
                </h3>
                
                {addressError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-600">{addressError}</p>
                  </div>
                )}

                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">
                        First Name <span className="text-gray-400">(optional)</span>
                      </label>
                      <input
                        type="text"
                        value={deliveryAddress.firstName}
                        onChange={(e) => setDeliveryAddress({...deliveryAddress, firstName: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                        placeholder="First name"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">
                        Last Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={deliveryAddress.lastName}
                        onChange={(e) => setDeliveryAddress({...deliveryAddress, lastName: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                        placeholder="Last name"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      Street Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={deliveryAddress.address}
                      onChange={(e) => setDeliveryAddress({...deliveryAddress, address: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                      placeholder="House number and street name"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      Apartment, Suite, etc. <span className="text-gray-400">(optional)</span>
                    </label>
                    <input
                      type="text"
                      value={deliveryAddress.apartment}
                      onChange={(e) => setDeliveryAddress({...deliveryAddress, apartment: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                      placeholder="Apartment, suite, building, etc."
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">
                        City <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={deliveryAddress.city}
                        onChange={(e) => setDeliveryAddress({...deliveryAddress, city: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                        placeholder="City"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">
                        State <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={deliveryAddress.state}
                        onChange={(e) => setDeliveryAddress({...deliveryAddress, state: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm bg-white"
                        required
                      >
                        <option value="">Select State</option>
                        <option value="Maharashtra">Maharashtra</option>
                        <option value="Delhi">Delhi</option>
                        <option value="Karnataka">Karnataka</option>
                        <option value="Tamil Nadu">Tamil Nadu</option>
                        <option value="Gujarat">Gujarat</option>
                        <option value="Rajasthan">Rajasthan</option>
                        <option value="West Bengal">West Bengal</option>
                        <option value="Uttar Pradesh">Uttar Pradesh</option>
                        <option value="Punjab">Punjab</option>
                        <option value="Haryana">Haryana</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      PIN Code <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={deliveryAddress.pinCode}
                      onChange={(e) => setDeliveryAddress({...deliveryAddress, pinCode: e.target.value.replace(/\D/g, '').slice(0, 6)})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                      placeholder="6-digit PIN code"
                      maxLength="6"
                      required
                    />
                  </div>

                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={deliveryAddress.saveAddress}
                      onChange={(e) => setDeliveryAddress({...deliveryAddress, saveAddress: e.target.checked})}
                      className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                    />
                    <span className="text-xs text-gray-600">Save this information for next time</span>
                  </label>
                </div>
              </div>
              
              {/* Discount Code Section */}
              <div className="mb-6 pb-6 border-b">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Nanaki's Kaur Discount Code
                </label>
                {!discountApplied ? (
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={discountCode}
                      onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                      placeholder="Enter code"
                      className="flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                    <button
                      onClick={applyDiscountCode}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition font-semibold"
                    >
                      Apply
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-3">
                    <div>
                      <p className="text-sm font-semibold text-green-800">
                        Code Applied: {discountApplied.code}
                      </p>
                      <p className="text-xs text-green-600">
                        {discountApplied.discount_percentage}% discount from {discountApplied.angel_name}
                      </p>
                    </div>
                    <button
                      onClick={removeDiscountCode}
                      className="text-red-600 hover:text-red-700 text-sm font-semibold"
                    >
                      Remove
                    </button>
                  </div>
                )}
                {discountError && (
                  <p className="text-sm text-red-600 mt-2">{discountError}</p>
                )}
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex justify-between text-gray-600">
                  <span>Subtotal</span>
                  <span>{formatINR(cart.total)}</span>
                </div>
                {discountApplied && (
                  <div className="flex justify-between text-green-600 font-semibold">
                    <span>Discount ({discountApplied.discount_percentage}%)</span>
                    <span>-{formatINR(cart.total * 0.023)}</span>
                  </div>
                )}
                <div className="flex justify-between text-gray-600">
                  <span>GST (18%)</span>
                  <span>{formatINR((cart.total - (discountApplied ? cart.total * 0.023 : 0)) * 0.18)}</span>
                </div>
              </div>
              <div className="border-t pt-4 mb-6">
                <div className="flex justify-between text-2xl font-bold">
                  <span>Total</span>
                  <span className="text-purple-600" data-testid="cart-total">
                    {formatINR(
                      (cart.total - (discountApplied ? cart.total * 0.023 : 0)) * 1.18
                    )}
                  </span>
                </div>
              </div>

              {/* Order Confirmation — Offline payment & delivery */}
              <div className="mb-6 p-6 bg-gradient-to-br from-[#FBF7F2] to-[#F4ECE2] rounded-lg border-2 border-[#E8DCC8]">
                <h3 className="font-bold text-lg mb-3 text-[#2A1B2D]">
                  Place your order
                </h3>
                <p className="text-stone-700 text-sm mb-4 leading-relaxed">
                  Once you place this order, <strong>Nanaki Kaur&apos;s team will personally reach out</strong> to
                  collect payment offline through the brand&apos;s verified payment link, coordinate the delivery
                  to your saved address, and share invoices with everyone involved — you, the brand, and the
                  influencer who referred this sale.
                </p>
                <ul className="space-y-2 text-sm text-stone-700 mb-5">
                  <li className="flex items-start"><span className="text-[#B6594C] mr-2 mt-1">◆</span><span>No online payment is collected on this site</span></li>
                  <li className="flex items-start"><span className="text-[#B6594C] mr-2 mt-1">◆</span><span>Our team contacts you within 24 hours via phone / WhatsApp</span></li>
                  <li className="flex items-start"><span className="text-[#B6594C] mr-2 mt-1">◆</span><span>Pay only after you confirm the order with our team</span></li>
                </ul>

                <button
                  onClick={handleUpiPaymentComplete}
                  disabled={processing}
                  className="w-full bg-[#B6594C] text-white py-3 rounded-lg font-semibold hover:bg-[#9d4a40] transition disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="place-order-btn"
                >
                  {processing ? 'Placing your order...' : 'Place Order'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Thank You Modal for UPI Payment */}
      {showThankYouModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 animate-fade-in">
            {/* Success Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
              </div>
            </div>

            {/* Thank You Message */}
            <h2 className="text-3xl font-bold text-center mb-2 text-gray-800">
              Order placed.
            </h2>
            <p className="text-center text-gray-600 mb-6">
              Welcome to the <span className="font-semibold text-[#B6594C]">Nanaki Kaur</span> sisterhood.
            </p>

            {/* Order Details */}
            <div className="bg-[#FBF7F2] rounded-lg p-4 mb-6 border border-[#E8DCC8]">
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-700">Order Amount:</span>
                <span className="font-bold text-[#B6594C] text-xl">
                  {formatINR(discountApplied ? cart.total * 0.977 : cart.total)}
                </span>
              </div>
              {discountApplied && (
                <div className="text-sm text-green-700 text-center">
                  ✓ Influencer discount applied: {discountApplied.discount_percentage}%
                </div>
              )}
            </div>

            {/* Offline workflow — explained */}
            <div className="bg-[#FBF7F2] border border-[#E8DCC8] rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-[#2A1B2D] mb-3">
                What happens next
              </h3>
              <p className="text-sm text-stone-700 leading-relaxed mb-3">
                <strong>This is an offline-coordinated order.</strong> Once you&apos;ve placed it,
                Nanaki Kaur takes responsibility for everything that follows:
              </p>
              <ul className="space-y-2 text-sm text-stone-700">
                <li className="flex items-start">
                  <span className="text-[#B6594C] mr-2 mt-0.5">1.</span>
                  <span><strong>Payment collection</strong> — our team reaches out within 24 hours to collect payment offline using the brand&apos;s verified payment link</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#B6594C] mr-2 mt-0.5">2.</span>
                  <span><strong>Order delivery</strong> — we coordinate dispatch and delivery to your saved address</span>
                </li>
                <li className="flex items-start">
                  <span className="text-[#B6594C] mr-2 mt-0.5">3.</span>
                  <span><strong>Invoice to all</strong> — once delivered, a unified invoice is shared with you, the brand, and (if you used a code) the influencer who earned commission on this sale, so they can see their role in this order</span>
                </li>
              </ul>
            </div>

            {/* Contact Info */}
            <div className="text-center mb-6">
              <p className="text-sm text-gray-600">
                Questions? Reach us at:
              </p>
              <p className="font-semibold text-[#B6594C]">+91 99-30-11-18-23</p>
              <p className="text-xs text-gray-500 mt-1">jasjeevbhasin5757@gmail.com</p>
            </div>

            {/* Close Button */}
            <button
              onClick={() => {
                setShowThankYouModal(false);
                navigate('/');
              }}
              className="w-full bg-[#B6594C] text-white py-3 rounded-lg font-semibold hover:bg-[#9d4a40] transition"
              data-testid="thank-you-continue-btn"
            >
              Continue Shopping
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cart;